import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { LiveTrackingMap } from "../components/LiveTrackingMap";
import { Truck, MapPin, Link2, RefreshCw, Radio, Copy } from "lucide-react";

function relTime(iso) {
  if (!iso) return "no signal yet";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
const isLive = (iso) => iso && (Date.now() - new Date(iso).getTime()) < 5 * 60 * 1000;

export default function LiveTracking() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vehicles/live");
      setVehicles(data.vehicles || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [load]);

  async function copyLink(v) {
    let token = v.driver_token;
    try {
      if (!token) {
        const { data } = await api.post(`/vehicles/${v.id}/driver-token`);
        token = data.token;
        load();
      }
      const url = `${window.location.origin}/track-driver/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Driver link copied — send it to the driver on WhatsApp");
    } catch { toast.error("Could not create link"); }
  }

  const trackers = vehicles.filter((v) => v.has_location)
    .map((v) => ({ lat: v.lat, lng: v.lng, label: v.vehicle_number, trail: v.trail }));
  const liveCount = vehicles.filter((v) => isLive(v.updated_at)).length;

  return (
    <div data-testid="live-tracking-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Operations</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Live Fleet Tracking</h1>
          <p className="text-sm text-[#68665E] mt-2">
            Real-time location of your owned trucks · <span className="text-[#1f8a4c] font-semibold">{liveCount} live</span> of {vehicles.length}
          </p>
        </div>
        <button onClick={load} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="live-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <LiveTrackingMap trackers={trackers} height={540} />
        </div>
        <div className="space-y-3 lg:max-h-[540px] lg:overflow-y-auto pr-1">
          {loading ? (
            <div className="text-sm text-[#68665E]" data-testid="live-loading">Loading fleet…</div>
          ) : vehicles.length === 0 ? (
            <div className="card-surface p-6 text-center" data-testid="live-empty">
              <Truck className="w-8 h-8 text-[#C84B31]/40 mx-auto mb-2" />
              <div className="font-medium text-[#1A1A18]">No owned vehicles yet</div>
              <p className="text-sm text-[#68665E] mt-1">Mark a vehicle as "Owned" in Masters to track it here.</p>
            </div>
          ) : vehicles.map((v) => (
            <div key={v.id} className="card-surface p-4" data-testid={`live-vehicle-${v.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono font-bold text-[#1A1A18]">{v.vehicle_number}</span>
                {isLive(v.updated_at) ? (
                  <span className="badge-success" data-testid={`live-status-${v.id}`}><Radio className="w-3 h-3" /> Live</span>
                ) : (
                  <span className="badge-neutral">{v.has_location ? "Idle" : "No signal"}</span>
                )}
              </div>
              <div className="mt-2 space-y-1 text-sm text-[#68665E]">
                {v.lr_number && <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C84B31]" /> {v.lr_number}{v.lr_route ? ` · ${v.lr_route}` : ""}</div>}
                <div className="flex items-center gap-1.5"><span className="w-3.5" />Updated {relTime(v.updated_at)}{v.speed != null ? ` · ${Math.round((v.speed || 0) * 3.6)} km/h` : ""}</div>
                {v.has_location && <div className="data-num text-xs text-[#8A8676]">{v.lat?.toFixed(4)}, {v.lng?.toFixed(4)}</div>}
              </div>
              <button onClick={() => copyLink(v)} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C84B31] hover:underline" data-testid={`live-copy-link-${v.id}`}>
                {v.driver_token ? <Copy className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />} {v.driver_token ? "Copy driver link" : "Create driver link"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
